import { TranslateService } from '@ngx-translate/core';
import { UserNotification } from './user-notification.model';

/**
 * Resolves the text of a notification.
 *
 * The server stores i18n keys and parameters rather than a rendered sentence, so a grouped
 * notification can say "12 invitados han confirmado" the moment its counter moves, in
 * whatever language the reader uses. When the key is missing — old rows, or a key nobody
 * seeded yet — the stored text is used instead of showing the raw key.
 */
export function resolveNotificationTitle(notification: UserNotification, translate: TranslateService): string {
    return resolve(notification.title_key, notification, translate) ?? notification.title;
}

export function resolveNotificationMessage(notification: UserNotification, translate: TranslateService): string {
    return resolve(notification.message_key, notification, translate) ?? notification.message;
}

/**
 * Parameters of the message. Invalid JSON is treated as "no parameters": a broken payload
 * must not blank the notification.
 */
export function notificationParams(notification: UserNotification): Record<string, unknown> {
    if (!notification.params) {
        return {};
    }

    try {
        return JSON.parse(notification.params) as Record<string, unknown>;
    } catch {
        return {};
    }
}

function resolve(key: string | null, notification: UserNotification, translate: TranslateService): string | null {
    if (!key) {
        return null;
    }

    const params = notificationParams(notification);
    const count = notification.group_count ?? 1;

    // A grouped notification reads differently in the singular: "Ada ha confirmado" versus
    // "12 invitados han confirmado". The plural key is used only when it exists.
    const pluralKey = `${key}_plural`;
    const wanted = count > 1 && translate.instant(pluralKey) !== pluralKey ? pluralKey : key;

    const resolved = translate.instant(wanted, { ...params, count });

    // ngx-translate echoes the key back when it cannot find it.
    return resolved === wanted ? null : resolved;
}
